<template>
  <DataTable
  :loading="loading"
  :items="linesForTable"
  :headers="headers"
  :customHeaders="customHeaders"
  :customCells="customCells"
  :showSearch="false"
  :tableHeightCalculationData="tableHeightCalculationData"
  :propItemsPerPage="itemsPerPage"
  :disablePagination="disablePagination"
  :groupBy="propGroupBy"
  :groupByUnspecifiedText="$t('notSpecified')"
  :itemClass="itemClass"
  containerClass="pa-0 mt-n3"
  smallHeaders>
    <template #headerExpansion>
      <v-row align="center" justify="end" dense>
        <v-col cols="auto">
          <span>{{$t('expansion')}}</span>
        </v-col>

        <v-col cols="auto">
          <Icon :icon="'mdi-information-outline'" margin="" @icon-clicked="expansionInfoClicked"/>
        </v-col>
      </v-row>
    </template>

    <template #isLocked="{ item }">
      <Icon
      v-if="item.isLocked"
      :icon="'mdi-lock'"
      :iconColor="'secondary'"
      :tooltipText="$t('locked')"/>
    </template>

    <template #speciesGroup="{ item: rateSheetLine }" >
      <v-tooltip bottom color="secondary" v-if="getSpeciesGroupTooltip(rateSheetLine, accountId, scalingGroups?.speciesGroups)">
        <template #activator="{ on }">
          <v-chip v-if="matchColorFor(rateSheetLine, 'speciesGroup')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'speciesGroup')" v-on="on">
            {{ rateSheetLine.speciesGroup }}
          </v-chip>
          <span v-else :style="styleForItem(rateSheetLine)" v-on="on">
            {{ rateSheetLine.speciesGroup }}
          </span>
        </template>
        <span class="subtitle-1 white--text">{{ getSpeciesGroupTooltip(rateSheetLine, accountId, scalingGroups?.speciesGroups) }}</span>
      </v-tooltip>
      <v-chip v-else-if="matchColorFor(rateSheetLine, 'speciesGroup')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'speciesGroup')">
        {{ rateSheetLine.speciesGroup }}
      </v-chip>
      <span :style="styleForItem(rateSheetLine)" v-else>
        {{ rateSheetLine.speciesGroup }}
      </span>
    </template>

    <template #lengthGroup="{ item: rateSheetLine }" >
      <v-tooltip bottom color="secondary" v-if="getLengthGroupTooltip(rateSheetLine, accountId, scalingGroups?.lengthGroups)">
        <template #activator="{ on }">
          <v-chip v-if="matchColorFor(rateSheetLine, 'lengthGroup')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'lengthGroup')" v-on="on">
            {{ rateSheetLine.lengthGroup }}
          </v-chip>
          <span v-else :style="styleForItem(rateSheetLine)" v-on="on">
            {{ rateSheetLine.lengthGroup }}
          </span>
        </template>
        <span class="subtitle-1 white--text">{{ getLengthGroupTooltip(rateSheetLine, accountId, scalingGroups?.lengthGroups) }}</span>
      </v-tooltip>
      <v-chip v-else-if="matchColorFor(rateSheetLine, 'lengthGroup')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'lengthGroup')">
        {{ rateSheetLine.lengthGroup }}
      </v-chip>
      <span :style="styleForItem(rateSheetLine)" v-else>
        {{ rateSheetLine.lengthGroup }}
      </span>
    </template>

    <template #diameterGroup="{ item: rateSheetLine }">
      <v-tooltip bottom color="secondary" v-if="getDiameterGroupTooltip(rateSheetLine, accountId, scalingGroups?.diameterGroups)">
        <template #activator="{ on }">
          <v-chip v-if="matchColorFor(rateSheetLine, 'diameterGroup')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'diameterGroup')" v-on="on">
            {{ rateSheetLine.diameterGroup }}
          </v-chip>
          <span v-else v-on="on" :style="styleForItem(rateSheetLine)">
            {{ rateSheetLine.diameterGroup }}
          </span>
        </template>
        <span class="subtitle-1 white--text">{{ getDiameterGroupTooltip(rateSheetLine, accountId, scalingGroups?.diameterGroups) }}</span>
      </v-tooltip>
      <v-chip v-else-if="matchColorFor(rateSheetLine, 'diameterGroup')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'diameterGroup')">
        {{ rateSheetLine.diameterGroup }}
      </v-chip>
      <span :style="styleForItem(rateSheetLine)" v-else>
        {{ rateSheetLine.diameterGroup }}
      </span>
    </template>

    <template #grade="{ item: rateSheetLine }">
      <v-chip v-if="matchColorFor(rateSheetLine, 'grade')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'grade')">
        {{ rateSheetLine.grade }}
      </v-chip>
      <span v-else :style="styleForItem(rateSheetLine)">
        {{ rateSheetLine.grade }}
      </span>
    </template>

    <template #endUseSort="{ item: rateSheetLine }">
      <v-chip v-if="matchColorFor(rateSheetLine, 'endUseSort')" x-small outlined class="match-chip" :color="matchColorFor(rateSheetLine, 'endUseSort')">
        {{ rateSheetLine.endUseSort }}
      </v-chip>
      <span v-else :style="styleForItem(rateSheetLine)">
        {{ rateSheetLine.endUseSort }}
      </span>
    </template>

    <template #rate="{ item: rateSheetLine }">
      <div v-if="rateSheetLine.rate >= 0">
        <span v-if="!editingRates" :style="styleForItemRate(rateSheetLine)">
          {{ formatMoney(rateSheetLine.rate) }}
        </span>
        <MoneyTextField
          v-else
          :key="rateSheetLine[determineKey(rateSheetLine)]"
          :initialValue="rateSheetLine.rate"
          :label="$t('rate')"
          @val-changed="$emit('rate-changed', rateSheetLine[determineKey(rateSheetLine)], $event)"
          style="max-width: 5rem; justify-self: end;"
        />
      </div>
    </template>

    <template #expansion="{ item: rateSheetLine }">
      <span :style="styleForItem(rateSheetLine)">
        {{ formatPercentage(rateSheetLine.expansion) }}
      </span>
    </template>

    <template #actions="{ item }" v-if="crud">
      <v-row class="flex-nowrap" justify="end">
        <Icon
        v-if="showLockInActionColumn(item)"
        :icon="lockIcon(item)"
        :iconColor="lockColor(item)"
        @icon-clicked="lockClicked(item)"
        :tooltipText="getLockTooltip(item)"
        :disabled="loading"/>

        <Icon
        v-if="showEditInActionColumn(item)"
        icon="mdi-pencil"
        :tooltipText="$t('edit')"
        @icon-clicked="editClicked(item)"
        :disabled="loading"/>

        <Icon
        v-if="showSoftRemoveInActionColumn(item)"
        :icon="item.isDeleted ? 'mdi-arrow-u-left-top-bold' : 'mdi-close'"
        iconColor="secondary"
        @icon-clicked="removeClicked(item)"
        :tooltipText="item.isDeleted ? $t('restore'):$t('remove')"
        :disabled="loading"/>

        <Icon
        v-else-if="showDeleteInActionColumn(item)"
        icon="mdi-delete-forever"
        @icon-clicked="deleteClicked(item)"
        :tooltipText="$t('delete')"
        :disabled="loading"/>
      </v-row>
    </template>
  </DataTable>
</template>

<script>
import { rateSheetLineHeaders } from '@/headers/RateSheet'
import { RateSheetInteractionMode } from '@/components/activity/rate-sheet/RateSheet'
import { formatMoney, formatPercentage, correctPercentagesToOneHundred } from '@/utils/NumericMutations'
import { getSpeciesGroupTooltip } from '@/components/account/account-scaling/species-groups/SpeciesGroups'
import { getLengthGroupTooltip } from '@/components/account/account-scaling/length-groups/LengthGroups'
import { getDiameterGroupTooltip } from '@/components/account/account-scaling/diameter-groups/DiameterGroups'
import { rateSheetDefinitionLineSorter } from '@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitions'
import { mapGetters } from 'vuex'
import { sumBy } from 'lodash'

export default {
  name: 'RateSheetLineTable',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue')
  },

  props: {
    rateSheetInteractionMode: {
      type: Number,
      required: true
    },

    lines: {
      type: Array,
      required: true
    },

    accountId: {
      type: Number,
      required: true
    },

    loading: {
      type: Boolean,
      default: false
    },

    propGroupBy: {
      type: String,
      required: false,
      default: undefined
    },

    itemClass: {
      type: Function,
      required: false,
      default: () => 'angel-table-row'
    },

    tableHeightCalculationData: {
      type: Object,
      required: false,
      default: () => ({ auto: true })
    },

    expansion: {
      type: Object,
      required: false,
      default: null
    },

    linesAreFiltered: {
      type: Boolean,
      default: false
    },

    baseRateCostCalculationId: {
      type: Number,
      required: false,
      default: null
    },

    originalLines: {
      type: Array,
      required: true
    },

    editingRates: {
      type: Boolean,
      default: false
    },

    lineMatchResults: {
      type: Function,
      required: false,
      default: null
    },

    scalingGroups: {
      type: Object,
      required: false,
      default: null
    }
  },

  data: () => ({
    RateSheetInteractionMode,
    linesForTable: []
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountLengthGroups', 'accountDiameterGroups']),

    headers () {
      let headers = rateSheetLineHeaders(this.crud).getColumns()
      if (this.excludeLockedColumn) headers = headers.filter(h => h.value !== 'isLocked')
      headers = headers.filter(this.headerIsInUseByATableLine)
      if (this.expansion) {
        headers.splice(headers.length - 1, 0, {
          text: this.$t('expansion'),
          value: 'expansion',
          align: 'right'
        })
      }
      return headers
    },

    customCells () {
      return this.headers.map(h => ({
        slotName: h.value,
        value: h.value
      }))
    },

    customHeaders () {
      return [{
        slotName: 'headerExpansion',
        value: 'expansion'
      }]
    },

    excludeLockedColumn () {
      return this.lines.every(line => !line.isLocked)
    },

    crud () {
      return [RateSheetInteractionMode.RateSheetDefinition.Create,
        RateSheetInteractionMode.RateSheetDefinition.Edit,
        RateSheetInteractionMode.RateSheet.Create,
        RateSheetInteractionMode.RateSheet.Edit].includes(this.rateSheetInteractionMode)
    },

    isRateSheetDefinitionMode () {
      return [RateSheetInteractionMode.RateSheetDefinition.Create,
        RateSheetInteractionMode.RateSheetDefinition.Edit,
        RateSheetInteractionMode.RateSheetDefinition.View
      ].includes(this.rateSheetInteractionMode)
    },

    key () {
      return this.isRateSheetDefinitionMode ? 'rateSheetDefinitionLineId' : 'rateSheetLineId'
    },

    itemsPerPage () {
      return this.tableHeightCalculationData.auto ? 10 : -1
    },

    disablePagination () {
      return !this.tableHeightCalculationData.auto
    },

    rateLineIds () {
      return this.lines.map(line => line.rateSheetLineId)
    },

    originalRateLineIds () {
      return this.originalLines.map(line => line.rateSheetLineId)
    }
  },

  mounted () {
    this.setLinesForTable()
  },

  watch: {
    lines: {
      handler () {
        this.setLinesForTable()
      },
      deep: true
    },

    expansion: {
      handler () {
        this.setLinesForTable()
      },
      deep: true
    }
  },

  methods: {
    formatMoney,
    getSpeciesGroupTooltip,
    getLengthGroupTooltip,
    getDiameterGroupTooltip,
    formatPercentage,

    setLinesForTable () {
      const lines = this.lines
        .toSorted(rateSheetDefinitionLineSorter)
        .map(line => ({
          speciesGroup: line.speciesGroup?.label,
          lengthGroup: line.lengthGroup?.label,
          diameterGroup: line.diameterGroup?.label,
          grade: line.grade?.name,
          endUseSort: line.endUseSort?.name,
          rateSheetLineId: line.rateSheetLineId,
          rateSheetDefinitionLineId: line.rateSheetDefinitionLineId,
          isLocked: line.isLocked,
          isDeleted: line.isDeleted,
          rate: line.rate,
          isIneligible: line.isIneligible,
          rateFromPreexistingSheet: line.rateFromPreexistingSheet,
          rateFromDefinition: line.rateFromDefinition,
          expansion: this.getExpansionForLine(line),
          uuid: line.uuid,
          matchResults: this.lineMatchResults?.(line)
        }))
      if (this.expansion) {
        const correctionArray = lines.map(l => ({ id: l.rateSheetLineId, percentage: l.expansion }))

        const corrected = correctPercentagesToOneHundred(correctionArray, 'percentage')

        const correctedMap = corrected
          .reduce((acc, c) => { acc[c.id] = c.percentage; return acc }, {})

        const correctedLines = lines.map(l => ({
          ...l,
          expansion: correctedMap[l.rateSheetLineId]
        }))

        this.linesForTable = correctedLines
        return
      }

      this.linesForTable = lines
    },

    showLockInActionColumn (item) {
      return [RateSheetInteractionMode.RateSheet.Edit,
        RateSheetInteractionMode.RateSheet.Create].includes(this.rateSheetInteractionMode) &&
      !item.isDeleted &&
      !item.isIneligible
    },

    showEditInActionColumn (item) {
      return this.crud && !item.isDeleted && !item.isIneligible
    },

    showSoftRemoveInActionColumn (item) {
      return [RateSheetInteractionMode.RateSheet.Edit,
        RateSheetInteractionMode.RateSheet.Create].includes(this.rateSheetInteractionMode) &&
      this.lineHasExistingId(item) &&
      !item.isIneligible
    },

    showDeleteInActionColumn (item) {
      return !item.isIneligible
    },

    getLockTooltip (line) {
      return line.isLocked
        ? this.$t('rateSheetLineUnlockTooltip')
        : this.$t('rateSheetLineLockTooltip')
    },

    editClicked (line) {
      this.$emit('edit', { line, unformattedLine: this.getUnformattedLine(line) })
    },

    deleteClicked (line) {
      this.$emit('delete', { line, unformattedLine: this.getUnformattedLine(line) })
    },

    removeClicked (line) {
      this.$emit('remove', { line, unformattedLine: this.getUnformattedLine(line) })
    },

    lockClicked (line) {
      this.$emit('lock', { line, unformattedLine: this.getUnformattedLine(line) })
    },

    getUnformattedLine (line) {
      return this.lines.find(l => l[this.key] === line[this.key])
    },

    matchColorFor (item, column) {
      const matches = item.matchResults?.[column]
      if (matches === undefined) return null
      return matches ? 'success' : 'error'
    },

    lockColor (item) {
      return item.isLocked ? 'secondary' : 'grey'
    },

    lockIcon (item) {
      return item.isLocked ? 'mdi-lock' : 'mdi-lock-open-variant'
    },

    styleForItemRate (item) {
      const rateDoesntMatchExistingFromDefinition = (item.rateFromDefinition !== undefined && item.rate !== item.rateFromDefinition)
      const rateDoesntMatchExistingFromPreexistingSheet = (item.rateFromPreexistingSheet !== undefined && item.rate !== item.rateFromPreexistingSheet)

      return {
        fontWeight: this.crud &&
          !item.isDeleted &&
          !item.isIneligible &&
          (rateDoesntMatchExistingFromDefinition || rateDoesntMatchExistingFromPreexistingSheet)
          ? 'bold'
          : '',

        textDecoration: this.crud && (item.isDeleted || item.isIneligible) ? 'line-through' : '',
        color: this.crud && item.isIneligible ? 'rgba(0, 0, 0, 0.4)' : ''
      }
    },

    styleForItem (item) {
      return {
        textDecoration: this.crud && (item.isDeleted || item.isIneligible) ? 'line-through' : '',
        color: this.crud && item.isIneligible ? 'rgba(0, 0, 0, 0.4)' : ''
      }
    },

    headerIsInUseByATableLine (header) {
      return this.linesForTable.some(line => {
        return !['speciesGroup', 'lengthGroup', 'diameterGroup', 'grade', 'endUseSort'].includes(header.value) ||
        line[header.value] != null
      })
    },

    lineHasExistingId (line) {
      return !!line.rateSheetDefinitionLineId || !!line.rateSheetLineId
    },

    getExpansionForLine (line) {
      if (!this.expansion || !line.rateSheetLineId) return undefined
      const totalGrossVolume = sumBy(this.expansion.rateLines.filter(rl => rl.costCalculationId === this.baseRateCostCalculationId || this.originalRateLineIds.includes(rl.rateSheetLineId)), rateLine => rateLine.grossVolume)
      const grossVolumeForLine = sumBy(this.expansion.rateLines
        .filter(se => se.rateSheetLineId === line.rateSheetLineId), rateLine => rateLine.grossVolume)

      return parseFloat((grossVolumeForLine / totalGrossVolume * 100).toFixed(2))
    },

    expansionInfoClicked () {
      this.$emit('expansion-info-clicked')
    },

    determineKey (rateSheetLine) {
      if (rateSheetLine.rateSheetDefinitionLineId !== null && rateSheetLine.rateSheetDefinitionLineId !== undefined) {
        return 'rateSheetDefinitionLineId'
      } else if (rateSheetLine.rateSheetLineId !== null && rateSheetLine.rateSheetLineId !== undefined) {
        return 'rateSheetLineId'
      } else {
        return 'uuid'
      }
    }
  }
}
</script>

<style scoped>
.match-chip::before {
  opacity: 0.12;
}
</style>
